import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { CrewTextBox } from 'components/devextreme/crewTextBox'
import { TextBox } from 'devextreme-react'

import { SearchChatRoomResultList } from './components/searchChatRoomResultList/searchChatRoomResultList'
import { SearchChatRoom } from './components/searchChatRoomResultList/components/searchChatRoomResultListItem/searchChatRoomResultListItem'
import { debounce } from 'lodash'
import { useLazySearchChatRoomsQuery } from '@crew/apis/chat/chatApis'
import { SEARCH_TIMEOUT_MSEC } from '@crew/configs/constants'
import { useShowApiErrors } from 'hooks/useShowApiErrors'

export type SearchChatRoomPanelProps = {
  keyword: string
  onKeywordChange: (value: string) => void
}

// 検索結果の最大取得件数
const LIMIT = 100

// チャットルーム検索パネル
export const SearchChatRoomPanel: FC<SearchChatRoomPanelProps> = memo(
  (props) => {
    // チャット検索（グループチャット、インスタントチャンネル、DM）API
    const [lazySearchChatRoomsQuery] = useLazySearchChatRoomsQuery()

    // チャット検索APIの結果保持用のstate
    const [searchChatRooms, setSearchChatRooms] = useState<
      SearchChatRoom[] | null
    >(null)

    // キーワード検索のテキストボックスのref
    const keywordTextBoxRef = useRef<TextBox>(null)

    const [showApiErrors] = useShowApiErrors()

    // チャット検索ボックスの入力変更時に、チャットルーム検索を実行する
    const debouncedSearch = useMemo(
      () =>
        debounce(async (value: string) => {
          if (!value) {
            // 値がない場合は、検索状態と検索結果をクリアして終了
            setSearchChatRooms(null)
            return
          }

          try {
            const result = await lazySearchChatRoomsQuery({
              keyword: value,
              limit: LIMIT,
            }).unwrap()

            // 古い応答が来ても上書きされないように、キーワードが一致する場合のみ結果をセットする
            const input = keywordTextBoxRef.current?.instance.option('text')

            if (input !== value) {
              return
            }

            // 検索結果をセット
            setSearchChatRooms(result.chatRooms)
          } catch (error) {
            showApiErrors(error)
          }
        }, SEARCH_TIMEOUT_MSEC),
      [lazySearchChatRoomsQuery, showApiErrors]
    )

    // アンマウント時に、実行中のdebouncedSearchをキャンセルする
    useEffect(() => {
      return () => {
        debouncedSearch.cancel()
      }
    }, [debouncedSearch])

    // チャット検索ボックスの入力変更時のハンドラ
    const handleKeywordChange = useCallback(
      (value: string) => {
        props.onKeywordChange(value)
        debouncedSearch(value)
      },
      [debouncedSearch, props]
    )

    return (
      <div className="flex flex-col">
        <div className="px-2 py-1.5">
          {/* チャットルーム検索ボックス */}
          <CrewTextBox
            value={props.keyword}
            onValueChange={handleKeywordChange}
            showClearButton
            mode="search"
            ref={keywordTextBoxRef}
            valueChangeEvent="input change"
            // ここはbgがcrew-gray-900なため、通常通り配置すると背景色が同じになり、目立たなくなる
            // そのため、カスタムクラスを適用して背景色と境界線色を調整する
            className="custom-textbox"
          />
        </div>

        {/* 検索結果一覧 */}
        {props.keyword ? (
          <SearchChatRoomResultList chatRooms={searchChatRooms} />
        ) : null}
      </div>
    )
  }
)
